import React, { useState } from 'react';
import { X, Download, Copy, Check, Image as ImageIcon, Loader2 } from 'lucide-react';
import { copyImageDataUrlToClipboard, downloadImageDataUrl } from '../utils/exportImage';

export default function ImagePreviewModal({
  isOpen,
  onClose,
  imageDataUrl,
  filename = 'protocol-stack.png',
  title = 'Protocol Image Preview',
  loading = false
}) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(null);

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (!imageDataUrl) return;
    setCopyError(null);

    const result = await copyImageDataUrlToClipboard(imageDataUrl);

    if (result.success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } else {
      setCopyError('Could not copy automatically. You can right-click the image to copy.');
      setTimeout(() => setCopyError(null), 4000);
    }
  };

  const handleDownload = () => {
    if (!imageDataUrl) return;
    downloadImageDataUrl(imageDataUrl, filename);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-fadeIn">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-3xl w-full p-6 shadow-2xl relative flex flex-col max-h-[90vh]">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center space-x-3 mb-4 flex-shrink-0">
          <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400 border border-purple-500/20">
            <ImageIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">{title}</h2>
            <p className="text-xs text-slate-400">
              Copy to clipboard or download the non-transparent image
            </p>
          </div>
        </div>

        {/* Image Preview Container */}
        <div className="flex-1 overflow-auto rounded-xl bg-slate-950 border border-slate-800 p-4 flex items-center justify-center min-h-[250px] relative">
          {loading ? (
            <div className="flex flex-col items-center justify-center space-y-3 py-12 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              <span className="text-xs font-medium">Generating high resolution image...</span>
            </div>
          ) : imageDataUrl ? (
            <img
              src={imageDataUrl}
              alt="Protocol Export Preview"
              className="max-w-full h-auto rounded-lg shadow-lg border border-slate-800/80 object-contain max-h-[60vh]"
            />
          ) : (
            <div className="text-xs text-slate-500">Failed to load image preview.</div>
          )}

          {/* Copy Notification Toast */}
          {copied && (
            <div className="absolute top-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg flex items-center space-x-1.5 animate-fadeIn">
              <Check className="w-4 h-4" />
              <span>Copied to Clipboard!</span>
            </div>
          )}

          {copyError && (
            <div className="absolute bottom-4 bg-amber-500/90 text-slate-950 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-lg animate-fadeIn">
              {copyError}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="pt-4 border-t border-slate-800 mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0">
          <div className="text-[11px] text-slate-500 hidden sm:block">
            PNG Image format • Non-transparent slate theme
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full sm:w-auto">
            {/* Copy & Download side-by-side */}
            <div className="grid grid-cols-2 sm:flex sm:items-center gap-2.5 w-full sm:w-auto">
              <button
                type="button"
                onClick={handleCopy}
                disabled={loading || !imageDataUrl}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-200 font-bold text-xs border border-slate-700 transition flex items-center justify-center space-x-2"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-purple-400" />}
                <span>{copied ? 'Copied!' : 'Copy Image'}</span>
              </button>

              <button
                type="button"
                onClick={handleDownload}
                disabled={loading || !imageDataUrl}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500 disabled:opacity-40 text-white font-bold text-xs shadow-lg shadow-purple-500/20 transition flex items-center justify-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download Image</span>
              </button>
            </div>

            {/* Close button - below on mobile, first on desktop */}
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium transition sm:order-first"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
